import React, { useRef, useState } from "react";
import { userApi, apiError } from "@/lib/userApi";
import { toast } from "sonner";
import { Image as ImageIcon, Send, X, Megaphone, Loader2, MessageCircle, Users, MessageSquareOff } from "lucide-react";
import { stormSurge } from "@/lib/pulse";
import { Avatar } from "@/components/community/ChatList";
import { MentionInput } from "@/components/feed/MentionInput";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const preview = (m) => `${BACKEND}${m.url}`;

/** Caja para publicar: texto + fotos/videos (y anuncios si eres admin). */
export const FeedComposer = ({ me, onPosted }) => {
  const [text, setText] = useState("");
  const [media, setMedia] = useState([]);
  const [kind, setKind] = useState("post");
  const [mentions, setMentions] = useState([]);
  const [commentsMode, setCommentsMode] = useState("all");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const COMMENT_MODES = [
    { key: "all", icon: MessageCircle, label: "Todos comentan" },
    { key: "friends", icon: Users, label: "Solo amigos" },
    { key: "nobody", icon: MessageSquareOff, label: "Sin comentarios" },
  ];
  const nextMode = () => {
    const i = COMMENT_MODES.findIndex((m) => m.key === commentsMode);
    setCommentsMode(COMMENT_MODES[(i + 1) % COMMENT_MODES.length].key);
  };
  const mode = COMMENT_MODES.find((m) => m.key === commentsMode);

  const attach = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      for (const f of Array.from(files).slice(0, 4)) {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await userApi.post("/community/media", fd);
        setMedia((m) => [...m, data]);
      }
    } catch (e) { toast.error(apiError(e, "No se pudo subir el archivo")); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const publish = async () => {
    if (!text.trim() && media.length === 0) return toast.error("Escribe algo o sube una foto");
    setBusy(true);
    try {
      await userApi.post("/feed", {
        text: text.trim(), media_ids: media.map((m) => m.media_id), kind, mentions,
        comments_mode: commentsMode,
      });
      setText(""); setMedia([]); setKind("post"); setMentions([]); setCommentsMode("all");
      stormSurge();
      toast.success("Publicado");
      onPosted?.();
    } catch (e) { toast.error(apiError(e, "No se pudo publicar")); }
    finally { setBusy(false); }
  };

  return (
    <div className="glass-card p-4" data-testid="feed-composer">
      <div className="flex gap-3">
        <Avatar person={me} size={40} />
        <MentionInput testId="feed-text" multiline value={text} onChange={setText}
          onMentions={setMentions}
          placeholder={`¿Qué quieres compartir, ${me?.name?.split(" ")[0] || "compa"}? Usa @ para etiquetar`} />
      </div>

      {media.length > 0 && (
        <div className="flex gap-2 mt-3 flex-wrap" data-testid="feed-media-preview">
          {media.map((m) => (
            <div key={m.media_id} className="relative">
              {m.mime?.startsWith("video/") ? (
                <video src={preview(m)} className="w-24 h-24 object-cover rounded-lg border border-white/12" />
              ) : (
                <img src={preview(m)} alt={m.name} className="w-24 h-24 object-cover rounded-lg border border-white/12" />
              )}
              <button onClick={() => setMedia((x) => x.filter((y) => y.media_id !== m.media_id))}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-black/80 border border-white/20 flex items-center justify-center">
                <X className="w-3 h-3 text-white" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between mt-3 gap-2">
        <div className="flex items-center gap-2">
          <button data-testid="feed-attach" onClick={() => fileRef.current?.click()} disabled={busy}
            className="glass-btn px-3 py-2 rounded-xl text-xs flex items-center gap-1.5">
            <ImageIcon className="w-3.5 h-3.5" /> Foto o video
          </button>
          <input ref={fileRef} type="file" accept="image/*,video/*" multiple className="hidden"
            data-testid="feed-file" onChange={(e) => attach(e.target.files)} />
          <button data-testid="feed-comments-mode" onClick={nextMode} title="¿Quién puede comentar?"
            className={`glass-btn ${commentsMode !== "all" ? "glass-btn-red" : ""} px-3 py-2 rounded-xl text-xs flex items-center gap-1.5`}>
            <mode.icon className="w-3.5 h-3.5" /> {mode.label}
          </button>
          {me?.is_admin && (
            <button data-testid="feed-kind-anuncio" onClick={() => setKind(kind === "anuncio" ? "post" : "anuncio")}
              className={`glass-btn ${kind === "anuncio" ? "glass-btn-red" : ""} px-3 py-2 rounded-xl text-xs flex items-center gap-1.5`}>
              <Megaphone className="w-3.5 h-3.5" /> Anuncio
            </button>
          )}
        </div>
        <button data-testid="feed-publish" onClick={publish} disabled={busy}
          className="glass-btn glass-btn-red px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Publicar
        </button>
      </div>
    </div>
  );
};

export default FeedComposer;
